<template>
    <v-modal v-if="show" title="Add Countries" size="extra-large" @close="close" @enter="init">
        <div slot="contents">
            <div class="modal-content">
                <r-input placeholder="Search Countries" v-model="countryFilter"/>
            </div>
            <div class="modal-content">
                <ul class="list list-group list-divided">
                    <li class="list-item align-items-center">
                        <r-checkbox
                            class="no-flex"
                            :value="true"
                            :disabled="!!restOfTheWorld"
                            v-model="restOfTheWorldSelected"
                            @input="toggleRestOfTheWorld()"
                        >
                            <div class="display-flex" slot="label">
                                <div class="flex no-flex"><v-icon class="margin-right-s" icon="world"/></div>
                                <div class="flex">
                                    <p>Rest of the World <span v-if="restOfTheWorld">({{restOfTheWorld.name}})</span></p>
                                    <p class="text-muted">Select this option to create a default shipping zone for countries that were not specified in other zones</p>
                                </div>
                            </div>
                        </r-checkbox>
                    </li>
                </ul>
            </div>
            <div class="modal-content is-scrollable">
                <ul class="list list-group list-relaxed list-divided no-flex">
                    <li class="list-item" v-for="(continent, continentCode) in countriesByContinent" :key="continentCode">
                        <div>
                            <r-checkbox
                                :value="true"
                                :fuzzy="continentsState[continentCode] ? continentsState[continentCode].fuzzy : false"
                                :disabled="restOfTheWorldSelected"
                                v-model="continentsState[continentCode].value"
                                @input="toggleContinent(continentCode, continentsState[continentCode].value)"
                            >
                                <h3 slot="label">{{continentCodeToName(continentCode)}}</h3>
                            </r-checkbox>
                            <div class="divider"></div>
                            <ul class="list list-group list-divided margin-left-default">
                                <li class="list-item align-items-center" v-for="country in continent" :key="country.countryCode">
                                    <r-checkbox
                                        class="no-flex"
                                        :value="true"
                                        :disabled="restOfTheWorldSelected || checkOtherZonesSelectedCountries(country.countryCode)"
                                        v-model="countriesState[country.countryCode]"
                                        @input="toggleCountry(country.countryCode)"
                                    >
                                        <div class="display-flex align-items-center" slot="label">
                                            <v-flags class="margin-right-s" :country-code="country.countryCode"/> <p>{{country.countryName}} {{checkOtherZonesSelectedCountries(country.countryCode, true)}}</p>
                                        </div>
                                    </r-checkbox>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <template slot="actions">
            <r-button type="primary" @click="apply">Apply</r-button>
        </template>
    </v-modal>
</template>

<script>
    import vModal from '@/components/modal/v-modal';
    import {vIcon} from '@/components/form';
    import COUNTRIES from '@/data/countries';
    import vFlags from '@/components/v-flags';
    import _ from '@/common/helpers';

    export default {
        components: {
            vModal, vFlags, vIcon,
        },
        props: {
            show: {
                type: Boolean,
                default: false,
                required: true,
            },
            selectedCountries: {
                validator: value => value === null || Array.isArray(value),
                default: () => [],
            },
            otherZones: Array,
        },
        computed: {
            restOfTheWorld() {
                return this.otherZones.find(zone => zone.isDefault);
            },
            filteredList() {
                if (this.countryFilter) {
                    return COUNTRIES.filter(
                        ({countryName}) => new RegExp(this.countryFilter, 'i').test(countryName),
                    );
                }
                return COUNTRIES;
            },
            countriesByContinent() {
                const continents = {};
                this.filteredList.forEach((country) => {
                    const list = continents[country.continent];
                    if (list) {
                        list.push(country);
                    } else {
                        continents[country.continent] = [country];
                    }
                });
                return continents;
            },
        },
        data() {
            return {
                countryFilter: null,
                continentsState: {},
                countriesState: {},
                restOfTheWorldSelected: false,
            };
        },
        methods: {
            continentCodeToName(code) {
                const continentNameMap = {
                    EU: 'Europe',
                    AS: 'Asia',
                    CA: 'Central America',
                    AF: 'Africa',
                    AN: 'Antarctica',
                    SA: 'South America',
                    OC: 'Oceania',
                    NA: 'North America',
                };
                return continentNameMap[code];
            },
            init() {
                this.countriesState = this.initCountriesState();
                this.continentsState = this.initContinentsState();
                this.restOfTheWorldSelected = false;
            },
            initCountriesState() {
                let countriesState = {};
                if (this.selectedCountries !== null) {
                    COUNTRIES.forEach((country) => {
                        countriesState = {
                            ...countriesState,
                            [country.countryCode]: this.selectedCountries.includes(country.countryCode),
                        };
                    });
                }
                return countriesState;
            },
            initContinentsState() {
                const continentsState = {};
                COUNTRIES.forEach((country) => {
                    if (!continentsState[country.continent]) {
                        continentsState[country.continent] = {value: false, fuzzy: this.isContinentFuzzy(country.continent)};
                    }
                });
                return continentsState;
            },
            isContinentFuzzy(code) {
                const countriesOnContinent = COUNTRIES.filter(country => country.continent === code);
                const selectedCountriesOnContinent = Object.keys(this.countriesState)
                    .filter(key => countriesOnContinent.find(
                        country => country.countryCode === key && this.countriesState[key],
                    ));
                return !_.isEmpty(selectedCountriesOnContinent);
            },
            toggleContinent(code, value) {
                const countriesOnContinent = COUNTRIES.filter(country => country.continent === code);
                countriesOnContinent.forEach((country) => {
                    if (!this.checkOtherZonesSelectedCountries(country.countryCode)) {
                        this.countriesState[country.countryCode] = value;
                    }
                });
                this.continentsState[code].fuzzy = false;
            },
            toggleCountry(code) {
                const countryToggled = COUNTRIES.find(country => country.countryCode === code);
                if (countryToggled) {
                    this.continentsState[countryToggled.continent].fuzzy = this.isContinentFuzzy(countryToggled.continent);
                }
            },
            checkOtherZonesSelectedCountries(code, name = false) {
                if (name) {
                    const zone = this.otherZones.find(item => item.countries !== null && item.countries.includes(code));
                    if (zone) {
                        return `(${zone.name})`;
                    }
                    return '';
                }
                return this.otherZones.some((zone) => {
                    if (zone.countries !== null) {
                        return zone.countries.includes(code);
                    }
                    return false;
                });
            },
            toggleRestOfTheWorld() {
                const continents = Object.keys(this.continentsState);
                continents.forEach((continent) => {
                    this.continentsState[continent] = {value: false, fuzzy: false};
                });
                this.countriesState = {};
            },
            apply() {
                const selectedCountries = Object.keys(this.countriesState)
                    .filter(key => this.countriesState[key]);
                if (this.restOfTheWorldSelected) {
                    this.$emit('selected', null);
                } else {
                    this.$emit('selected', selectedCountries);
                }
                this.close();
            },
            close() {
                this.$emit('close');
            },
        },
        created() {
            this.init();
        },
    };
</script>
